import { useState } from "react";
import { Link } from "react-router-dom";
import type { FilingTermChange } from "@mosaic/shared";
import { FACT_LABEL, factDisplay } from "../lib/format.js";
import { filingHref } from "../lib/citations.js";

/**
 * One filing, and what it changed about the situation's terms.
 *
 * **Collapsed it names the terms; opened it shows the values and the quote.** A reader
 * scanning asks "did this one move anything", which the term names answer, and a page of
 * block quotes would push the timeline down.
 *
 * **The row stays a link and the caret is its own control**, a sibling button outside the
 * anchor, so the filing opens in the reader as everywhere else in Mosaic.
 */
export function FilingRow({
  accession,
  formType,
  filingDate,
  role,
  isPrimary = true,
  documentCount,
  ingestState,
  changes,
}: {
  accession: string;
  formType: string;
  filingDate: string;
  role: string;
  /**
   * Whether this situation is what the filing is about.
   *
   * Defaulted true because the safe failure is one-directional: a relevant-only filing shown
   * as a subject would have a reader expecting its terms here.
   */
  isPrimary?: boolean;
  documentCount: number;
  ingestState: string;
  changes: FilingTermChange[];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className={`filingrow${open ? " open" : ""}`}>
      <div className="filinghd">
        {changes.length > 0 ? (
          <button
            className="filingcaret"
            aria-expanded={open}
            aria-label={open ? "Hide what this filing changed" : "Show what this filing changed"}
            onClick={() => setOpen(!open)}
          >
            {open ? "▾" : "▸"}
          </button>
        ) : (
          <span className="filingcaret blank">·</span>
        )}
        <Link className="filing" to={`/filings/${accession}`}>
          <span className="ft">{formType}</span>
          <span className="fd">{filingDate}</span>
          <span className="fr">{role}</span>
          {/*
            Said on the row: a filing that contributed no terms otherwise looks unread,
            when its terms belong to the separation it is about.
          */}
          {!isPrimary && (
            <span className="fmention" title="Relevant to this separation, but about another">
              mentions
            </span>
          )}
          <span className="fn">
            {documentCount} doc{documentCount === 1 ? "" : "s"} · {ingestState}
          </span>
        </Link>
        {/*
          The terms this filing moved, named rather than counted: a count collapses a
          revised distribution date and a filled-in employee count into one figure.
        */}
        {changes.length > 0 && (
          <span className="filingupd">
            {changes.map((c) => (
              <span key={c.kind} className={`updk${c.previous ? "" : " fill"}`}>
                {FACT_LABEL[c.kind] ?? c.kind}
              </span>
            ))}
          </span>
        )}
      </div>

      {open && (
        <div className="filingdelta">
          {changes.map((c) => (
            <div className="drow" key={c.kind}>
              <div className="dk">{FACT_LABEL[c.kind] ?? c.kind}</div>
              <div className="dv">
                {c.previous ? (
                  <>
                    <span className="was">{factDisplay(c.kind, c.previous.value)}</span>
                    <span className="arrow">→</span>
                    <span className="now">{factDisplay(c.kind, c.value)}</span>
                  </>
                ) : (
                  <>
                    <span className="fill">{factDisplay(c.kind, c.value)}</span>
                    <span className="firstly">first stated</span>
                  </>
                )}
                {/* The filing's own hedge, kept beside the value it qualifies. */}
                {c.tentative && <span className="assumed">assumed</span>}
                {c.quote && (
                  <Link
                    className="dq"
                    to={filingHref(accession, { documentId: c.documentId, charStart: c.charStart })}
                  >
                    “{c.quote}”
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
